import React from 'react'
import { Alert, type AlertVariant } from '@enamelai/design-system'
import { NotificationPriority } from '@dataTypes/notification.types'
import './NotificationsStyle.css'

interface NotificationCardProps {
  priority: NotificationPriority
  title: string
  date: string
  time: string
  message: string
  actionButtonText: string
  patient_id?: string
  isRead?: boolean
  onMarkAsRead?: () => void
  onAction?: () => void
}

const getAlertVariant = (priority: NotificationPriority): AlertVariant => {
  switch (priority) {
    case NotificationPriority.CRITICAL:
      return 'critical'
    case NotificationPriority.URGENT:
      return 'urgent'
    default:
      return 'info'
  }
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  priority,
  title,
  date,
  time,
  message,
  actionButtonText,
  isRead = false,
  onMarkAsRead,
  onAction,
}) => {
  const variant = getAlertVariant(priority)
  const markAsLabel = isRead ? 'Mark as unread' : 'Mark as read'
  // Board design: Critical & Urgent show a secondary + primary action, Info
  // shows a single primary action. Mark as read/unread is always reachable
  // from the "more" menu so Info cards keep that capability.
  const showSecondaryAction = variant !== 'info'

  return (
    <Alert
      layout="board"
      variant={variant}
      title={title}
      timestamp={`${date} • ${time}`}
      description={message}
      unread={!isRead}
      className="notification-alert"
      menuItems={[
        {
          id: 'toggle-read',
          label: markAsLabel,
          onSelect: () => onMarkAsRead?.(),
        },
      ]}
      secondaryAction={
        showSecondaryAction
          ? { label: markAsLabel, onClick: () => onMarkAsRead?.() }
          : undefined
      }
      primaryAction={
        actionButtonText
          ? { label: actionButtonText, onClick: () => onAction?.() }
          : undefined
      }
    />
  )
}

export default NotificationCard
